import { createContext, useContext, type CSSProperties, type ReactNode } from 'react';
import { RotateCw } from 'lucide-react';

/**
 * Tiny timeline engine for the tool-card scenes. A scene declares its length
 * (`TimeProvider`) and what happens at which second; each animated piece turns
 * that into one CSS keyframes rule (registered once, by content) on a shared
 * style sheet. The inline style of every piece is its FINAL frame, so with
 * reduced motion (animations off) the scene simply shows its result.
 */

export type Css = CSSProperties;
export type Key = readonly [t: number, css: Css];

const TimeContext = createContext(8);
export const TimeProvider = TimeContext.Provider;
export const useT = () => useContext(TimeContext);

const FADE = 0.22; // appear / disappear
const OUT = 0.45; // everything fades out before the loop restarts
const STEP = 0.06; // cursor glyph swaps

/* ---------- keyframes registry ---------- */

const known = new Set<string>();
let sheet: CSSStyleSheet | null = null;

function toCss(css: Css): string {
  return Object.entries(css)
    .map(([k, v]) => `${k.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}:${String(v)}`)
    .join(';');
}

function keyframes(T: number, keys: readonly Key[]): string {
  const sorted = [...keys].sort((a, b) => a[0] - b[0]);
  const body = sorted
    .map(([t, css]) => `${((Math.min(Math.max(t, 0), T) / T) * 100).toFixed(3)}%{${toCss(css)}}`)
    .join('');
  let h = 5381;
  for (let i = 0; i < body.length; i++) h = ((h * 33) ^ body.charCodeAt(i)) >>> 0;
  const name = `ta-${h.toString(36)}`;
  if (!known.has(name)) {
    known.add(name);
    if (typeof document !== 'undefined') {
      if (!sheet) {
        const el = document.createElement('style');
        el.setAttribute('data-tool-animations', '');
        document.head.appendChild(el);
        sheet = el.sheet;
      }
      sheet?.insertRule(`@keyframes ${name}{${body}}`, sheet.cssRules.length);
    }
  }
  return name;
}

/** className + style of an animated element: final frame inline, keyframes on top. */
export function animProps(T: number, keys: readonly Key[], final: Css, extra?: Css) {
  return {
    className: 'ta-a',
    style: {
      ...final,
      ...extra,
      animationName: keyframes(T, keys),
      animationDuration: `${T}s`,
    } as CSSProperties,
  };
}

/** A `<g>` with arbitrary keyframes. */
export function A({
  keys,
  final,
  extra,
  children,
}: {
  keys: readonly Key[];
  final: Css;
  extra?: Css;
  children?: ReactNode;
}) {
  const T = useT();
  return <g {...animProps(T, keys, final, extra)}>{children}</g>;
}

/* ---------- visibility ---------- */

/**
 * Visible from `a` to `b` (default: until the end of the loop). `rise` also
 * slides it 3 px. `final` is whether the reduced-motion frame shows it
 * (default: only if it stays until the end).
 */
export function Show({
  a = 0,
  b,
  rise,
  final,
  children,
}: {
  a?: number;
  b?: number;
  rise?: boolean;
  final?: boolean;
  children?: ReactNode;
}) {
  const T = useT();
  const hid: Css = rise ? { opacity: 0, transform: 'translateY(3px)' } : { opacity: 0 };
  const shown: Css = rise ? { opacity: 1, transform: 'translateY(0)' } : { opacity: 1 };
  const keys: Key[] = [];
  if (a <= 0) keys.push([0, shown]);
  else keys.push([0, hid], [a, hid], [a + FADE, shown]);
  if (b === undefined) keys.push([T - OUT, shown], [T, hid]);
  else keys.push([b, shown], [b + FADE, hid], [T, hid]);
  const keep = final ?? b === undefined;
  return (
    <A keys={keys} final={keep ? shown : hid}>
      {children}
    </A>
  );
}

/** Visible during several `[from, to]` windows (hard-ish steps). */
export function Vis({ windows, children }: { windows: readonly (readonly [number, number])[]; children?: ReactNode }) {
  const T = useT();
  const on: Css = { opacity: 1 };
  const off: Css = { opacity: 0 };
  const keys: Key[] = [[0, windows[0] && windows[0][0] <= 0 ? on : off]];
  for (const [a, b] of windows) {
    if (a > 0) keys.push([a, off], [a + STEP, on]);
    keys.push([b, on], [Math.min(b + STEP, T), off]);
  }
  keys.push([T, off]);
  return (
    <A keys={keys} final={off}>
      {children}
    </A>
  );
}

/* ---------- movement ---------- */

/** `[arrive, x, y, hold]`: be at (x, y) from `arrive` for `hold` seconds (default 0.5). */
export type Way = readonly [t: number, x: number, y: number, hold?: number];
const tr = (x: number, y: number) => `translate(${x}px, ${y}px)`;

export function Track({
  way,
  rest,
  children,
}: {
  way: readonly Way[];
  /** The reduced-motion position (default: the last waypoint). */
  rest?: readonly [number, number];
  children?: ReactNode;
}) {
  const T = useT();
  const keys: Key[] = [];
  const first = way[0];
  const last = way[way.length - 1];
  if (!first || !last) return <g>{children}</g>;
  if (first[0] > 0) keys.push([0, { transform: tr(first[1], first[2]) }]);
  for (const [t, x, y, h = 0.5] of way) {
    keys.push([t, { transform: tr(x, y) }], [Math.min(t + h, T), { transform: tr(x, y) }]);
  }
  keys.push([T, { transform: tr(last[1], last[2]) }]);
  const r = rest ?? [last[1], last[2]];
  return (
    <A keys={keys} final={{ transform: tr(r[0], r[1]) }}>
      {children}
    </A>
  );
}

/** Click ripple at (x, y) at second `t` (the editor's dark brand turquoise unless told otherwise). */
export function Rip({ t, x, y, color = '#3dd6c7', r = 10, sw = 2 }: { t: number; x: number; y: number; color?: string; r?: number; sw?: number }) {
  const T = useT();
  const small = 'scale(.4)';
  const keys: Key[] = [
    [0, { opacity: 0, transform: small }],
    [t, { opacity: 0, transform: small }],
    [t + 0.05, { opacity: 0.9, transform: 'scale(.5)' }],
    [Math.min(t + 0.7, T), { opacity: 0, transform: 'scale(1.7)' }],
    [T, { opacity: 0, transform: 'scale(1.7)' }],
  ];
  return (
    <A keys={keys} final={{ opacity: 0 }} extra={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
      <circle cx={x} cy={y} r={r} fill="none" stroke={color} strokeWidth={sw} />
    </A>
  );
}

/* ---------- cursors ---------- */

export type CursorKind = 'arrow' | 'cross' | 'move' | 'open' | 'fist' | 'rotate';
const INK = '#111111';
const EDGE = '#ffffff';

/** One OS cursor, hotspot at the origin. */
function Glyph({ k }: { k: CursorKind }) {
  const outline = { stroke: EDGE, strokeLinejoin: 'round', strokeLinecap: 'round' } as const;
  const ink = { stroke: INK, strokeLinejoin: 'round', strokeLinecap: 'round', fill: 'none' } as const;
  if (k === 'cross') {
    const d = 'M-6 0 H6 M0 -6 V6';
    return (
      <g>
        <path d={d} {...outline} strokeWidth="3" />
        <path d={d} {...ink} strokeWidth="1.1" />
      </g>
    );
  }
  if (k === 'move') {
    const d = 'M0 -7 V7 M-7 0 H7';
    const heads = 'M0 -9.5 L-2.6 -6.4 H2.6 Z M0 9.5 L-2.6 6.4 H2.6 Z M-9.5 0 L-6.4 -2.6 V2.6 Z M9.5 0 L6.4 -2.6 V2.6 Z';
    return (
      <g>
        <path d={d} {...outline} strokeWidth="3" />
        <path d={heads} {...outline} strokeWidth="2.4" fill={EDGE} />
        <path d={d} {...ink} strokeWidth="1.1" />
        <path d={heads} fill={INK} />
      </g>
    );
  }
  if (k === 'rotate') {
    return (
      <g>
        <RotateCw x={-8} y={-8} width={16} height={16} stroke="#000000" strokeOpacity=".75" strokeWidth={4.2} />
        <RotateCw x={-8} y={-8} width={16} height={16} stroke="#ffffff" strokeWidth={2.2} />
      </g>
    );
  }
  if (k === 'open' || k === 'fist') {
    const hand = { fill: EDGE, stroke: INK, strokeWidth: 0.8, strokeLinejoin: 'round' } as const;
    return k === 'open' ? (
      <g transform="translate(0 1)">
        <rect x="-5.4" y="-7.2" width="2.4" height="7" rx="1.2" {...hand} />
        <rect x="-2.7" y="-8.6" width="2.4" height="8.4" rx="1.2" {...hand} />
        <rect x="0" y="-8.2" width="2.4" height="8" rx="1.2" {...hand} />
        <rect x="2.7" y="-6.8" width="2.4" height="6.6" rx="1.2" {...hand} />
        <rect x="-5.8" y="-2.6" width="11.2" height="8.6" rx="3.2" {...hand} />
        <rect x="-8.2" y="-1.4" width="2.4" height="5.4" rx="1.2" transform="rotate(-28 -7 1.3)" {...hand} />
      </g>
    ) : (
      <g transform="translate(0 1)">
        <rect x="-5.8" y="-3.6" width="11.6" height="9" rx="3.2" {...hand} />
        <path d="M-2.9 -3.6 V-0.6 M0 -3.6 V-0.6 M2.9 -3.6 V-0.6" {...ink} strokeWidth="0.7" />
        <rect x="-8" y="-0.6" width="2.4" height="4.6" rx="1.2" transform="rotate(-24 -6.8 1.7)" {...hand} />
      </g>
    );
  }
  return (
    <path
      d="M0 0 L0 13 L3.6 9.6 L6.2 15 L8.4 14 L5.8 8.8 L10.5 8.5 Z"
      fill={INK}
      stroke={EDGE}
      strokeWidth="1.1"
      strokeLinejoin="round"
    />
  );
}

/**
 * The pointer. `way` is where its tip goes; `kinds` is which glyph it wears
 * from each second on. It fades out before the loop restarts and is not part
 * of the reduced-motion frame.
 */
export function Cursor({
  way,
  kinds,
  size = 1,
  children,
}: {
  way: readonly Way[];
  kinds: readonly (readonly [number, CursorKind])[];
  /** Glyph scale (the tip stays on the path): 1 at card size, smaller where 1 unit is several pixels. */
  size?: number;
  /** Extra things that travel with the tip (a key chip). */
  children?: ReactNode;
}) {
  const T = useT();
  const set = [...new Set(kinds.map(k => k[1]))];
  return (
    <Show final={false}>
      <Track way={way}>
        <g transform={size === 1 ? undefined : `scale(${size})`}>
          {set.map(kind => {
            const windows = kinds.flatMap(([t, k], i): [number, number][] =>
              k === kind ? [[t, kinds[i + 1]?.[0] ?? T]] : [],
            );
            return (
              <Vis key={kind} windows={windows}>
                <Glyph k={kind} />
              </Vis>
            );
          })}
        </g>
        {children}
      </Track>
    </Show>
  );
}

/** Dashed selection rectangle that grows from (x, y) to (x + w, y + h) between `a` and `b`, gone at `gone`. */
export function Marquee({ x, y, w, h, a, b, gone }: { x: number; y: number; w: number; h: number; a: number; b: number; gone: number }) {
  const T = useT();
  const s0: Css = { opacity: 0, width: '0px', height: '0px' };
  const s1: Css = { opacity: 1, width: '0px', height: '0px' };
  const s2: Css = { opacity: 1, width: `${w}px`, height: `${h}px` };
  const keys: Key[] = [
    [0, s0],
    [a, s0],
    [a + 0.03, s1],
    [b, s2],
    [gone, s2],
    [gone + FADE, { ...s2, opacity: 0 }],
    [T, { ...s2, opacity: 0 }],
  ];
  const p = animProps(T, keys, { opacity: 0 });
  return <rect x={x} y={y} width={w} height={h} fill="rgba(255,255,255,.1)" stroke="#ffffff" strokeWidth="1" strokeDasharray="3 2" {...p} />;
}
